import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  createCategoryQuery,
  getCategoryListQuery,
  updateCategoryQuery,
  deleteCategoryQuery,
  getProfileQuery
} from "../../Graphql/querries/categoryQuery";
import { getProductListQuery } from "../../Graphql/querries/productQuery";
import { setCategoryList } from "../../store/slices/categorySlice";
import {
  CreateCategoryResponse,
  GetCategoryListResponse,
  UpdateCategoryResponse,
  DeleteCategoryResponse
} from "../../Graphql/types/category";
import { CategoryFormData } from "../../ProductMFE/Category/types/categoryTypes";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import type { RootState } from "../../store/store";
import { setUserDetails, type UserDetails } from "../../store/slices/userSlice";
import {
  Category,
  GetCategoryListRequest,
  UpdateCategoryRequest,
  GetProfileResponse
} from "../../ProductMFE/Category/types/categoryTypes";
import type {
  GetProductListResponse,
  Product
} from "../../ProductMFE/Products/types/products";
import { enrichCategoriesWithProductCount } from "../../ProductMFE/Category/utils/categoryUtils";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

const PRODUCT_COUNT_PAGE_SIZE = 100;

/** Fetch all catalog products so category rows can show accurate product counts. */
async function fetchAllProductsForCounts(
  setAlert: (alert: import("@kivunova/kivufrontendcommon").AlertBanner) => void
): Promise<Product[]> {
  const products: Product[] = [];
  let page = 0;
  let totalPages = 1;

  try {
    while (page < totalPages) {
      const response = await fetchGraphQL<GetProductListResponse>(
        getProductListQuery,
        { input: { page, size: PRODUCT_COUNT_PAGE_SIZE } },
        setAlert
      );

      const result = response.listProducts;
      if (!result || result.code !== API_RESPONSE_CODE.OK || !result.products) {
        break;
      }

      products.push(...result.products);
      totalPages = Math.max(result.totalPages ?? 1, 1);
      page += 1;
    }
  } catch {
    return products;
  }

  return products;
}

/**
 * useGetCategoryList
 * Fetch list of categories
 */
export const useGetCategoryList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Category[], Error, GetCategoryListRequest>({
    mutationKey: ["getCategoryList"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching category list",
        buildLogContext(
          "categories",
          "get-category-list",
          userId,
          companyId,
          undefined // no storeId for categories
        )
      );

      try {
        const response: GetCategoryListResponse = await fetchGraphQL(
          getCategoryListQuery,
          { input },
          setAlert
        );

        const { categories, code } = response.listCategories;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch category list",
            buildLogContext(
              "categories",
              "get-category-list",
              userId,
              companyId,
              undefined // no storeId for categories
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          throw new Error(`Failed to fetch category list: ${code}`);
        }

        // ❌ Logically broken success response
        if (!categories) {
          captureAppException(
            "API returned OK but categories array missing",
            new Error("API returned OK but categories array missing"),
            buildLogContext(
              "categories",
              "get-category-list",
              userId,
              companyId
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          throw new Error("API returned OK but categories array missing");
        }

        // ✅ Success — product counts are not on listCategories; derive from full catalog
        const productList = await fetchAllProductsForCounts(setAlert);
        const enriched = enrichCategoriesWithProductCount(
          categories,
          productList
        );
        dispatch(setCategoryList(enriched));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Category list fetched successfully",
          buildLogContext(
            "categories",
            "get-category-list",
            userId,
            companyId,
            undefined, // categories are not store-scoped
            {
              categoryCount: enriched.length
            }
          )
        );

        return enriched;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch category list",
          error,
          buildLogContext("categories", "get-category-list", userId, companyId)
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw error;
      }
    }
  });
};

/**
 * useCreateCategory
 * Create a new category
 */
export const useCreateCategory = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const queryClient = useQueryClient();

  return useMutation<string | null, Error, CategoryFormData>({
    mutationKey: ["createCategory"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating category",
        buildLogContext(
          "categories",
          "create-category",
          userId,
          companyId,
          undefined, // categories are not store-scoped
          {
            categoryName: input.name
          }
        )
      );

      try {
        // Omit parentId when empty so backend treats parent as optional (no "parentId cannot be null")
        const cleanedInput: {
          name: string;
          description?: string;
          parentId?: string;
        } = {
          name: input.name
        };
        if (
          input.description != null &&
          String(input.description).trim() !== ""
        ) {
          cleanedInput.description = String(input.description).trim();
        }
        if (input.parentId != null && String(input.parentId).trim() !== "") {
          cleanedInput.parentId = String(input.parentId).trim();
        }

        const response: CreateCategoryResponse = await fetchGraphQL(
          createCategoryQuery,
          { input: cleanedInput },
          setAlert
        );

        const createResult = response?.createCategory;

        // ❌ Unexpected response shape
        if (!createResult) {
          captureAppException(
            "Create category failed: missing createCategory",
            new Error("Create category failed: missing createCategory"),
            buildLogContext(
              "categories",
              "create-category",
              userId,
              companyId,
              undefined,
              {
                categoryName: input.name
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ⚠️ Expected API/business failure
        if (createResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Category creation rejected by API",
            buildLogContext(
              "categories",
              "create-category",
              userId,
              companyId,
              undefined, // no storeId for categories
              {
                apiCode: String(createResult.code),
                categoryName: input.name
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: createResult.code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        const categoryId = createResult.id;

        // ❌ Logically broken success response
        if (!categoryId) {
          captureAppException(
            "API returned OK but category ID missing",
            new Error("API returned OK but category ID missing"),
            buildLogContext(
              "categories",
              "create-category",
              userId,
              companyId,
              undefined, // categories are not store-scoped
              {
                categoryName: input.name
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Category created successfully",
          buildLogContext(
            "categories",
            "create-category",
            userId,
            companyId,
            undefined, // no storeId for categories
            {
              categoryId
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return categoryId;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to create category",
          error,
          buildLogContext(
            "categories",
            "create-category",
            userId,
            companyId,
            undefined, // no storeId for categories
            {
              categoryName: input.name
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw error;
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCategoryList"] });
    }
  });
};

/**
 * useUpdateCategory
 * Update an existing category
 */
export const useUpdateCategory = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const queryClient = useQueryClient();

  return useMutation<string | null, Error, UpdateCategoryRequest>({
    mutationKey: ["updateCategory"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating category",
        buildLogContext(
          "categories",
          "update-category",
          userId,
          companyId,
          undefined, // no storeId for categories
          {
            categoryId: input.id
          }
        )
      );

      try {
        const response: UpdateCategoryResponse = await fetchGraphQL(
          updateCategoryQuery,
          { input },
          setAlert
        );

        const updateResult = response?.updateCategory;

        // ❌ Unexpected response shape
        if (!updateResult) {
          captureAppException(
            "Update category failed: missing updateCategory",
            new Error("Update category failed: missing updateCategory"),
            buildLogContext(
              "categories",
              "update-category",
              userId,
              companyId,
              undefined,
              {
                categoryId: input.id
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ⚠️ Expected API/business failure
        if (updateResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Category update rejected by API",
            buildLogContext(
              "categories",
              "update-category",
              userId,
              companyId,
              undefined, // no storeId for categories
              {
                apiCode: String(updateResult.code),
                categoryId: input.id
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: updateResult.code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        const categoryId = updateResult.id;

        // ❌ Logically broken success response
        if (!categoryId) {
          captureAppException(
            "API returned OK but category ID missing",
            new Error("API returned OK but category ID missing"),
            buildLogContext(
              "categories",
              "update-category",
              userId,
              companyId,
              undefined,
              {
                categoryId: input.id
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Category updated successfully",
          buildLogContext(
            "categories",
            "update-category",
            userId,
            companyId,
            undefined, // no storeId for categories
            {
              categoryId
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return categoryId;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to update category",
          error,
          buildLogContext(
            "categories",
            "update-category",
            userId,
            companyId,
            undefined, // no storeId for categories
            {
              categoryId: input.id
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw error;
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCategoryList"] });
    }
  });
};

/**
 * useDeleteCategory
 * Delete an existing category
 */
export const useDeleteCategory = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { id: string }>({
    mutationKey: ["deleteCategory"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started deleting category",
        buildLogContext(
          "categories",
          "delete-category",
          userId,
          companyId,
          undefined, // no storeId for categories
          {
            categoryId: input.id
          }
        )
      );

      try {
        const response: DeleteCategoryResponse = await fetchGraphQL(
          deleteCategoryQuery,
          { input },
          setAlert
        );

        const deleteResult = response?.deleteCategory;

        // ❌ Unexpected response shape
        if (deleteResult === undefined || deleteResult === null) {
          captureAppException(
            "Delete category failed: missing deleteCategory",
            new Error("Delete category failed: missing deleteCategory"),
            buildLogContext(
              "categories",
              "delete-category",
              userId,
              companyId,
              undefined, // no storeId for categories
              {
                categoryId: input.id
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return false;
        }

        // ⚠️ Expected API/business failure
        if (deleteResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Category deletion rejected by API",
            buildLogContext(
              "categories",
              "delete-category",
              userId,
              companyId,
              undefined, // no storeId for categories
              {
                apiCode: String(deleteResult.code),
                categoryId: input.id
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: deleteResult.code as unknown as ALERT_BANNER_CODE
          });
          return false;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Category deleted successfully",
          buildLogContext(
            "categories",
            "delete-category",
            userId,
            companyId,
            undefined, // categories are not store-scoped
            {
              categoryId: input.id
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return true;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to delete category",
          error,
          buildLogContext(
            "categories",
            "delete-category",
            userId,
            companyId,
            undefined, // categories are not store-scoped
            {
              categoryId: input.id
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw error;
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCategoryList"] });
    }
  });
};

/**
 * useGetProfile
 * PROFILE - USER
 */
export const useGetProfile = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<GetProfileResponse, Error>({
    mutationKey: ["profile"],
    mutationFn: () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching profile",
        buildLogContext("users", "get-profile", userId, companyId)
      );

      return fetchGraphQL(getProfileQuery, { input: {} }, setAlert);
    },
    onSuccess: (response) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      if (!response.getProfile) {
        captureAppException(
          "Profile fetch failed: missing getProfile",
          new Error("Profile fetch failed: missing getProfile"),
          buildLogContext("users", "get-profile", userId, companyId)
        );
        setAlert({
          code: ALERT_BANNER_CODE.INTERNAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
        return;
      }

      if (response.getProfile.code === API_RESPONSE_CODE.OK) {
        dispatch(
          setUserDetails(response.getProfile.user as unknown as UserDetails)
        );
        sendAppLog(
          LOG_LEVEL.INFO,
          "Profile fetched successfully",
          buildLogContext("users", "get-profile", userId, companyId)
        );
      } else {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Profile fetch rejected by API",
          buildLogContext(
            "users",
            "get-profile",
            userId,
            companyId,
            undefined, // no storeId for profile fetch
            {
              apiCode: String(response.getProfile.code)
            }
          )
        );
        setAlert({
          code:
            (response.getProfile.code as ALERT_BANNER_CODE) ||
            ALERT_BANNER_CODE.INTERNAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
      }
    },
    onError: (error) => {
      captureAppException(
        "Failed to fetch profile",
        error,
        buildLogContext(
          "users",
          "get-profile",
          userDetails?.id,
          userDetails?.companyId
        )
      );
      setAlert({
        code: ALERT_BANNER_CODE.INTERNAL_ERROR,
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR
      });
    }
  });
};
